import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FilterDescription } from 'app/components/Viewer/components/RightPanel/FilterParametrization';
import {
  makeSyncReduxWithLocalStorageObserver,
  restoreFromLocalStorage,
} from 'app/dataManagement/syncReduxStateWithLocalStorage';
import {
  LoadSharedUserConfigAction,
  SHARED_USER_CONFIG_LOADED,
} from 'app/redux/sharedUserConfig/actions';
import { ProcessingFiltersAssociationBySpecieAndRegion } from 'app/utils/PROCESSING_OPTIONS';
import observeStore from 'app/utils/redux/observeStore';
import _ from 'lodash';
import { Store } from 'redux';

export const PROCESSING_LOCAL_STORAGE_KEY = 'redux.processing';

export type StoredFilterDescription = FilterDescription & { id: string };

export type ProcessingState = {
  filters?: StoredFilterDescription[];
  filtersAssociationByProfile?: ProcessingFiltersAssociationBySpecieAndRegion; // We only store the filter IDs here
};

const processingSlice = createSlice({
  name: 'processing',
  initialState: () =>
    Object.freeze<ProcessingState>({
      filters: [],
      ...restoreFromLocalStorage(PROCESSING_LOCAL_STORAGE_KEY, {}),
    }),
  reducers: {
    setFilters: {
      reducer(state: ProcessingState, action: PayloadAction<StoredFilterDescription[]>) {
        state.filters = action.payload;
      },
      prepare(filters: StoredFilterDescription[]) {
        return { payload: filters, meta: { syncWithUserConfigurationAPI: 'processingFilters' } };
      },
    },
    updateFilter: {
      reducer(state: ProcessingState, action: PayloadAction<StoredFilterDescription>) {
        if (!state.filters) {
          state.filters = [];
        }
        const index = state.filters.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) {
          state.filters[index] = action.payload;
        } else {
          state.filters.push(action.payload);
        }
      },
      prepare: (filters: StoredFilterDescription) => ({
        payload: filters,
        meta: { syncWithUserConfigurationAPI: 'processingFilters' },
      }),
    },
    removeFilter: {
      reducer(state: ProcessingState, action: PayloadAction<string>) {
        if (!state.filters) {
          state.filters = [];
        }
        state.filters = state.filters.filter((f) => f.id !== action.payload);
      },
      prepare: (filterId: string) => ({
        payload: filterId,
        meta: { syncWithUserConfigurationAPI: 'processingFilters' },
      }),
    },
    setFiltersAssociationByProfile: {
      reducer: (
        state: ProcessingState,
        action: PayloadAction<ProcessingFiltersAssociationBySpecieAndRegion>
      ) => {
        state.filtersAssociationByProfile = action.payload;
      },
      prepare: (payload: ProcessingFiltersAssociationBySpecieAndRegion) => ({
        payload,
        meta: { syncWithUserConfigurationAPI: 'processingFiltersAssociationByProfile' },
      }),
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SHARED_USER_CONFIG_LOADED, (state, action: LoadSharedUserConfigAction) => {
      if (action.payload.processingFilters && !_.isEmpty(action.payload.processingFilters)) {
        state.filters = action.payload.processingFilters;
      }
      if (
        action.payload.processingFiltersAssociationByProfile &&
        !_.isEmpty(action.payload.processingFiltersAssociationByProfile)
      ) {
        state.filtersAssociationByProfile = action.payload.processingFiltersAssociationByProfile;
      }
    });
  },
});

export const { setFilters, updateFilter, removeFilter, setFiltersAssociationByProfile } =
  processingSlice.actions;

export const selectProcessing = (state: { processing: ProcessingState }): ProcessingState =>
  state.processing;
export const selectProcessingFilters = (state: { processing: ProcessingState }) =>
  state.processing.filters;
export const selectFiltersAssociationByProfile = (state: { processing: ProcessingState }) =>
  state.processing.filtersAssociationByProfile;

const processingReducer = processingSlice.reducer;

export type StoreWithProcessingState = { processing: ProcessingState };

export const attachProcessingWithLocalStorageSynchronizationObserver = (
  store: Store<StoreWithProcessingState>
) => {
  observeStore(
    store,
    selectProcessing,
    makeSyncReduxWithLocalStorageObserver(PROCESSING_LOCAL_STORAGE_KEY)
  );
};

export default processingReducer;
